import { logError } from '../shared/utils/logger';
import { BASE_URL, fetchWithAuth } from './client';

/**
 * Google Integration API Module
 * Handles Google OAuth, Gmail, and Calendar integration for Claw9
 */

// ==================== OAUTH ====================

/**
 * Get Google OAuth URL to start the connection flow
 * @param {Object} params - Connection parameters
 * @param {string} params.accountId - Claw9 account ID
 * @param {Array<string>} params.scopes - Scopes to request: 'gmail', 'calendar', or both
 * @returns {Promise<{success: boolean, auth_url?: string, state?: string, error?: string}>}
 */
export async function getGoogleOAuthUrl(params = {}) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/integrations/google/connect`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    account_id: params.accountId,
                    scopes: params.scopes || ['gmail', 'calendar'],
                    redirect_uri: `${window.location.origin}/integrations/google/callback`
                })
            }
        );

        const data = await response.json();

        if (!response.ok) {
            console.error('[Google API] OAuth URL failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to start Google connection'
            };
        }

        return {
            success: true,
            auth_url: data.auth_url,
            state: data.state
        };
    } catch (error) {
        logError('Google OAuth URL error:', error);
        return {
            success: false,
            error: error.message || 'Failed to start Google connection'
        };
    }
}

/**
 * Complete OAuth callback - exchange code for tokens
 * @param {string} code - OAuth authorization code
 * @param {string} state - OAuth state parameter
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function completeGoogleOAuth(code, state) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/integrations/google/callback`,
            {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ code, state })
            }
        );

        const data = await response.json();

        if (!response.ok) {
            console.error('[Google API] OAuth callback failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to complete Google connection'
            };
        }

        return { success: true, data };
    } catch (error) {
        logError('Google OAuth callback error:', error);
        return {
            success: false,
            error: error.message || 'Failed to complete Google connection'
        };
    }
}

/**
 * List connected Google accounts
 * @param {string} accountId - Claw9 account ID
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export async function listGoogleAccounts(accountId) {
    try {
        const url = accountId
            ? `${BASE_URL}/api/claw9/integrations/google/accounts?account_id=${encodeURIComponent(accountId)}`
            : `${BASE_URL}/api/claw9/integrations/google/accounts`;

        const response = await fetchWithAuth(url, {
            headers: { accept: 'application/json' }
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('[Google API] List accounts failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to load Google accounts'
            };
        }

        return { success: true, data: data.data || data.accounts || [] };
    } catch (error) {
        logError('Google list accounts error:', error);
        return {
            success: false,
            error: error.message || 'Failed to load Google accounts'
        };
    }
}

/**
 * Disconnect a Google account
 * @param {string} googleAccountId - Google account integration ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function disconnectGoogleAccount(googleAccountId) {
    try {
        const response = await fetchWithAuth(
            `${BASE_URL}/api/claw9/integrations/google/accounts/${encodeURIComponent(googleAccountId)}`,
            {
                method: 'DELETE',
                headers: { accept: 'application/json' }
            }
        );

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            console.error('[Google API] Disconnect failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to disconnect Google account'
            };
        }

        return { success: true };
    } catch (error) {
        logError('Google disconnect error:', error);
        return {
            success: false,
            error: error.message || 'Failed to disconnect Google account'
        };
    }
}

// ==================== GMAIL ====================

/**
 * List Gmail threads
 * @param {string} googleAccountId - Google account integration ID
 * @param {Object} params - Query parameters
 * @param {string} [params.label] - Filter by label (INBOX, SENT, etc.)
 * @param {string} [params.q] - Gmail search query
 * @param {number} [params.max_results] - Max results (default 20)
 * @param {string} [params.page_token] - Pagination token
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function listGmailThreads(googleAccountId, params = {}) {
    try {
        const queryParams = new URLSearchParams();
        if (params.label) queryParams.append('label', params.label);
        if (params.q) queryParams.append('q', params.q);
        if (params.max_results) queryParams.append('max_results', params.max_results);
        if (params.page_token) queryParams.append('page_token', params.page_token);

        const queryString = queryParams.toString();
        const url = `${BASE_URL}/api/claw9/integrations/google/${encodeURIComponent(googleAccountId)}/gmail/threads${queryString ? `?${queryString}` : ''}`;

        const response = await fetchWithAuth(url, {
            headers: { accept: 'application/json' }
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('[Google API] List threads failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to load emails'
            };
        }

        return { success: true, data };
    } catch (error) {
        logError('Gmail list threads error:', error);
        return {
            success: false,
            error: error.message || 'Failed to load emails'
        };
    }
}

/**
 * Get a single Gmail thread with messages
 * @param {string} googleAccountId - Google account integration ID
 * @param {string} threadId - Gmail thread ID
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function getGmailThread(googleAccountId, threadId) {
    try {
        const url = `${BASE_URL}/api/claw9/integrations/google/${encodeURIComponent(googleAccountId)}/gmail/threads/${encodeURIComponent(threadId)}`;

        const response = await fetchWithAuth(url, {
            headers: { accept: 'application/json' }
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('[Google API] Get thread failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to load email thread'
            };
        }

        return { success: true, data };
    } catch (error) {
        logError('Gmail get thread error:', error);
        return {
            success: false,
            error: error.message || 'Failed to load email thread'
        };
    }
}

/**
 * Send an email via Gmail
 * @param {string} googleAccountId - Google account integration ID
 * @param {Object} email - Email data
 * @param {string} email.to - Recipient email
 * @param {string} email.subject - Email subject
 * @param {string} email.body - Email body (plain text or HTML)
 * @param {string} [email.cc] - CC recipients
 * @param {string} [email.bcc] - BCC recipients
 * @param {string} [email.thread_id] - Thread ID for replies
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function sendGmailMessage(googleAccountId, email) {
    try {
        const url = `${BASE_URL}/api/claw9/integrations/google/${encodeURIComponent(googleAccountId)}/gmail/send`;

        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(email)
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('[Google API] Send email failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to send email'
            };
        }

        return { success: true, data };
    } catch (error) {
        logError('Gmail send error:', error);
        return {
            success: false,
            error: error.message || 'Failed to send email'
        };
    }
}

/**
 * Get Gmail labels
 * @param {string} googleAccountId - Google account integration ID
 * @returns {Promise<{success: boolean, data?: Array, error?: string}>}
 */
export async function getGmailLabels(googleAccountId) {
    try {
        const url = `${BASE_URL}/api/claw9/integrations/google/${encodeURIComponent(googleAccountId)}/gmail/labels`;

        const response = await fetchWithAuth(url, {
            headers: { accept: 'application/json' }
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('[Google API] Get labels failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to load labels'
            };
        }

        return { success: true, data: data.labels || data.data || [] };
    } catch (error) {
        logError('Gmail labels error:', error);
        return {
            success: false,
            error: error.message || 'Failed to load labels'
        };
    }
}

// ==================== CALENDAR ====================

/**
 * List calendar events
 * @param {string} googleAccountId - Google account integration ID
 * @param {Object} params - Query parameters
 * @param {string} [params.time_min] - Start date (ISO 8601)
 * @param {string} [params.time_max] - End date (ISO 8601)
 * @param {number} [params.max_results] - Max results
 * @param {string} [params.calendar_id] - Calendar ID (default: primary)
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function listCalendarEvents(googleAccountId, params = {}) {
    try {
        const queryParams = new URLSearchParams();
        if (params.time_min) queryParams.append('time_min', params.time_min);
        if (params.time_max) queryParams.append('time_max', params.time_max);
        if (params.max_results) queryParams.append('max_results', params.max_results);
        if (params.calendar_id) queryParams.append('calendar_id', params.calendar_id);

        const queryString = queryParams.toString();
        const url = `${BASE_URL}/api/claw9/integrations/google/${encodeURIComponent(googleAccountId)}/calendar/events${queryString ? `?${queryString}` : ''}`;

        const response = await fetchWithAuth(url, {
            headers: { accept: 'application/json' }
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('[Google API] List events failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to load calendar events'
            };
        }

        return { success: true, data };
    } catch (error) {
        logError('Calendar list events error:', error);
        return {
            success: false,
            error: error.message || 'Failed to load calendar events'
        };
    }
}

/**
 * Create a calendar event
 * @param {string} googleAccountId - Google account integration ID
 * @param {Object} event - Event data
 * @param {string} event.summary - Event title
 * @param {string} [event.description] - Event description
 * @param {string} event.start - Start time (ISO 8601)
 * @param {string} event.end - End time (ISO 8601)
 * @param {string} [event.location] - Event location
 * @param {Array<string>} [event.attendees] - Attendee emails
 * @param {string} [event.calendar_id] - Calendar ID (default: primary)
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function createCalendarEvent(googleAccountId, event) {
    try {
        const url = `${BASE_URL}/api/claw9/integrations/google/${encodeURIComponent(googleAccountId)}/calendar/events`;

        const response = await fetchWithAuth(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(event)
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('[Google API] Create event failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to create event'
            };
        }

        return { success: true, data };
    } catch (error) {
        logError('Calendar create event error:', error);
        return {
            success: false,
            error: error.message || 'Failed to create event'
        };
    }
}

/**
 * Update a calendar event
 * @param {string} googleAccountId - Google account integration ID
 * @param {string} eventId - Event ID
 * @param {Object} updates - Event updates
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function updateCalendarEvent(googleAccountId, eventId, updates) {
    try {
        const url = `${BASE_URL}/api/claw9/integrations/google/${encodeURIComponent(googleAccountId)}/calendar/events/${encodeURIComponent(eventId)}`;

        const response = await fetchWithAuth(url, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(updates)
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('[Google API] Update event failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to update event'
            };
        }

        return { success: true, data };
    } catch (error) {
        logError('Calendar update event error:', error);
        return {
            success: false,
            error: error.message || 'Failed to update event'
        };
    }
}

/**
 * Delete a calendar event
 * @param {string} googleAccountId - Google account integration ID
 * @param {string} eventId - Event ID
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function deleteCalendarEvent(googleAccountId, eventId) {
    try {
        const url = `${BASE_URL}/api/claw9/integrations/google/${encodeURIComponent(googleAccountId)}/calendar/events/${encodeURIComponent(eventId)}`;

        const response = await fetchWithAuth(url, {
            method: 'DELETE',
            headers: { accept: 'application/json' }
        });

        if (!response.ok) {
            const data = await response.json().catch(() => ({}));
            console.error('[Google API] Delete event failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to delete event'
            };
        }

        return { success: true };
    } catch (error) {
        logError('Calendar delete event error:', error);
        return {
            success: false,
            error: error.message || 'Failed to delete event'
        };
    }
}

/**
 * Get free/busy availability
 * @param {string} googleAccountId - Google account integration ID
 * @param {Object} params - Query parameters
 * @param {string} params.time_min - Start time (ISO 8601)
 * @param {string} params.time_max - End time (ISO 8601)
 * @returns {Promise<{success: boolean, data?: Object, error?: string}>}
 */
export async function getCalendarAvailability(googleAccountId, params) {
    try {
        const queryParams = new URLSearchParams();
        queryParams.append('time_min', params.time_min);
        queryParams.append('time_max', params.time_max);

        const url = `${BASE_URL}/api/claw9/integrations/google/${encodeURIComponent(googleAccountId)}/calendar/availability?${queryParams.toString()}`;

        const response = await fetchWithAuth(url, {
            headers: { accept: 'application/json' }
        });

        const data = await response.json();

        if (!response.ok) {
            console.error('[Google API] Get availability failed:', response.status, data);
            return {
                success: false,
                error: data.detail || data.error || 'Failed to load availability'
            };
        }

        return { success: true, data };
    } catch (error) {
        logError('Calendar availability error:', error);
        return {
            success: false,
            error: error.message || 'Failed to load availability'
        };
    }
}
